import { useState, useRef, useEffect } from 'react';
import { Filter, ChevronDown, X } from 'lucide-react';
import { getActiveFilterCount } from '../../utils/filterUtils';

/**
 * FilterMenu Component
 * 
 * A comprehensive filter menu component that provides dropdown filtering functionality.
 * Supports multiple filter types: merchants, conditions, availability, price range, domains, and delivery options.
 * 
 * Props:
 * - onFilterChange: Callback function called when filter options change
 * - filters: Current filter state object
 * - availableOptions: Available filter options based on current search results
 * - className: Additional CSS classes for styling
 */
function FilterMenu({ onFilterChange, filters = {}, availableOptions = {}, className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const filterButtonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.filter-menu-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!isOpen && filterButtonRef.current) {
      // Calculate position when opening (same as SortMenu and CurrencyMenu)
      const rect = filterButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
    
    setIsOpen(!isOpen);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFilterUpdate = (filterType, value, checked) => {
    const currentValues = filters[filterType] || [];
    
    let newValues;
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }

    const updatedFilters = {
      ...filters,
      [filterType]: newValues
    };

    onFilterChange(updatedFilters);
  };

  const handlePriceRangeUpdate = (type, value) => {
    console.log('Price range update:', { type, value, currentFilters: filters });
    
    const updatedFilters = {
      ...filters,
      priceRange: {
        ...(filters.priceRange || { min: null, max: null }),
        [type]: value === '' ? null : value
      }
    };

    console.log('Updated filters:', updatedFilters);
    onFilterChange(updatedFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      merchants: [],
      conditions: [],
      availability: [],
      priceRange: { min: null, max: null },
      domains: [],
      deliveryOptions: []
    };
    onFilterChange(clearedFilters);
  };

  const activeFilterCount = getActiveFilterCount(filters);

  const renderCheckboxGroup = (title, filterType, options) => {
    const isExpanded = expandedSections[filterType];
    const selectedValues = filters[filterType] || [];

    if (!options || options.length === 0) {
      return null;
    }

    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <button
          className="w-full px-4 py-2 text-right flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection(filterType)}
        >
          <span className="text-sm text-gray-700">{title}</span>
          <div className="flex items-center gap-2">
            {selectedValues.length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {selectedValues.length}
              </span>
            )}
            <ChevronDown 
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
            />
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-2 max-h-32 overflow-y-auto">
            {options.map(option => (
              <label key={option} className="flex items-center gap-2 py-1 cursor-pointer text-sm text-gray-700 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => handleFilterUpdate(filterType, option, e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPriceRange = () => {
    const isExpanded = expandedSections.priceRange;
    const priceRange = filters.priceRange || { min: null, max: null };
    const hasValue = priceRange.min !== null || priceRange.max !== null;

    console.log('Rendering price range:', { priceRange, filters });

    return (
      <div className="border-b border-gray-100 last:border-b-0">
        <button
          className="w-full px-4 py-2 text-right flex items-center justify-between hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection('priceRange')}
        >
          <span className="text-sm text-gray-700">טווח מחירים (₪)</span>
          <div className="flex items-center gap-2">
            {hasValue && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                פעיל
              </span>
            )}
            <ChevronDown 
              className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
            />
          </div>
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-2">
            <div className="flex gap-2 items-center" dir="ltr">
              <input
                type="number"
                placeholder="מינימום"
                value={priceRange.min || ''}
                onChange={(e) => handlePriceRangeUpdate('min', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-gray-500 text-xs">-</span>
              <input
                type="number"
                placeholder="מקסימום"
                value={priceRange.max || ''}
                onChange={(e) => handlePriceRangeUpdate('max', e.target.value)}
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-gray-900 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative filter-menu-container ${className}`}>
      <div
        ref={filterButtonRef}
        className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors"
        onClick={toggleDropdown}
      >
        <span className="text-sm text-gray-700">סינון</span>
        <div className="relative">
          <Filter className="w-5 h-5 text-gray-600" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </div>
      </div>

      {isOpen && (
        <div 
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[320px] z-[9999]"
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 9999,
            minWidth: '320px'
          }}
          dir="rtl"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-800">סינון תוצאות</h3>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                נקה הכל
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {renderCheckboxGroup('מוכר', 'merchants', availableOptions.merchants)}
            {renderCheckboxGroup('מצב המוצר', 'conditions', availableOptions.conditions)}
            {renderCheckboxGroup('זמינות', 'availability', availableOptions.availability)}
            {renderPriceRange()}
            {renderCheckboxGroup('אזור אמזון', 'domains', availableOptions.domains)}
            {renderCheckboxGroup('אפשרויות משלוח', 'deliveryOptions', availableOptions.deliveryOptions)}
          </div>

          {activeFilterCount === 0 && (
            <div className="px-4 py-3 text-center text-gray-500 text-xs">
              בחר קריטריונים לסינון התוצאות
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FilterMenu; 